import type { ICellRendererParams } from 'ag-grid-enterprise';
import { useMemo } from 'react';
import { useSecuritiesContext } from '../../contexts/SecuritiesContext';
import { Order } from '../../types/Order';
import type { Security } from '../../types/Security';
import { LegDirectionEnum } from '../../types/types';
import { isMultileg } from '../../utils';
import { isRowHeightExpanded } from '../BlotterTable/utils';
import { HStack, VStack } from '../Core';
import { DDHOrderIcon } from '../DDH/DDHOrderIcon';
import { SecurityRenderer } from '../SecurityRenderer';
import { resolveSecurityMarketLogo } from '../SecurityRenderer/resolveSecurityMarketLogo';
import { Text } from '../Text';
import { Tour } from '../Tour';
import { resolveRowTradedMarket } from './resolveRowTradedMarket';
import { type AgGridSecurityDecorationParams, type AgGridSecurityProps, SecurityToursEnum } from './types';

export function resolveShowDescription(
  showDescription: AgGridSecurityProps['showDescription'],
  params: AgGridSecurityDecorationParams
): boolean | undefined {
  return typeof showDescription === 'function' ? showDescription(params) : showDescription;
}

export function AgGridSecurity({
  value: unsanitizedValue,
  showIcon,
  showDescription,
  context,
  unifiedLiquidityIndicator: customUnifiedLiquidityIndicator,
  data,
  colorful,
  node,
  api,
  suffix,
  tours,
}: ICellRendererParams<any, any, any> & AgGridSecurityProps) {
  const value: string | undefined = typeof unsanitizedValue === 'string' ? unsanitizedValue : undefined;
  const security: Security | undefined = value ? context.current.securitiesBySymbol?.get(value) : undefined;

  const decorationParams = { data, security, context };
  const unifiedLiquidityIndicator =
    typeof customUnifiedLiquidityIndicator === 'function'
      ? customUnifiedLiquidityIndicator(decorationParams)
      : customUnifiedLiquidityIndicator;
  const shouldShowDescription = resolveShowDescription(showDescription, decorationParams);

  const effectiveSuffix = useMemo(() => {
    const customSuffix = typeof suffix === 'function' ? suffix({ data, security, context }) : suffix;
    if (customSuffix != null) {
      return customSuffix;
    }
    if (data instanceof Order && (data.isDDHParentOrder || data.isDDHHedgeOrder)) {
      return (
        <>
          {data.isDDHParentOrder && tours && tours.includes(SecurityToursEnum.DDH_ENABLED_ORDER) && (
            <>
              <Tour tour="v2.49.0 DDH Order Blotter" />
              <Tour tour="v2.49.0 DDH Order Blotter Filled Column" />
            </>
          )}
          <DDHOrderIcon order={data} />
        </>
      );
    }
  }, [context, data, security, suffix, tours]);

  const marketLogo = resolveSecurityMarketLogo({
    security,
    tradedMarket: resolveRowTradedMarket(data, context.current.marketAccountsByName),
  });

  const isRowExpanded = isRowHeightExpanded(node, api);
  if (value == null) {
    return null;
  }

  return (
    <HStack justifyContent="flex-start">
      <VStack h="100%" w="100%" gap="spacing4" alignItems="flex-start">
        <SecurityRenderer
          symbol={value}
          security={security}
          currency={security?.BaseCurrency}
          marketLogo={marketLogo}
          showIcon={showIcon}
          showDescription={shouldShowDescription}
          theme={context.current.theme}
          unifiedLiquidityIndicator={unifiedLiquidityIndicator}
          colorful={colorful}
          suffix={effectiveSuffix}
        />
        {isRowExpanded && security && isMultileg(security) && (
          <VStack alignItems="flex-start" gap="spacing4">
            <MultilegLegDirectionText security={security} legIndex={0} />
            <MultilegLegDirectionText security={security} legIndex={1} />
          </VStack>
        )}
      </VStack>
    </HStack>
  );
}

function MultilegLegDirectionText({ security, legIndex }: { security: Security; legIndex: 0 | 1 }) {
  const { securitiesBySymbol } = useSecuritiesContext();
  const leg = security.MultilegDetails?.Legs?.[legIndex];
  if (!leg) {
    return <></>;
  }

  const isDirectionSameAsParent = leg.Direction === LegDirectionEnum.Same;
  return (
    <Text color={isDirectionSameAsParent ? 'colorTextPositive' : 'colorTextNegative'}>
      {isDirectionSameAsParent ? '+1' : '-1'} {securitiesBySymbol.get(leg.Symbol)?.DisplaySymbol ?? leg.Symbol}
    </Text>
  );
}
